import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { hideProgress, logRequest } from 'app/main/shared/http-utils';
import { Observable } from 'rxjs';
import { Image } from '../model/image.model';
import { KeyValueDTO } from '../model/keyvalue.model';
import { IUser, UserCache, UserProperty, UserPropertyVM } from '../model/user.model';
import { RemovePropertyValueVM, UserImageBase64VM } from './../model/user.model';

export interface IUserAutoComplete {
    text: string;
    tenantId: string;
    limit: number;
    properties: UserProperty[];
    roleNames?: string[];
    onlyActiveUsers?: boolean;
    searchForMessaging?: boolean;
    includeRoleNames?: boolean;
}

@Injectable({ providedIn: 'root' })
export class UserService {
    private resourceUrl = 'uaa/api/users';

    constructor(private http: HttpClient) {}

    find(username: string): Observable<HttpResponse<IUser>> {
        return this.http.get<IUser>(`${this.resourceUrl}/${username}`, { observe: 'response' });
    }

    findById(userId: string): Observable<HttpResponse<IUser>> {
        return this.http.get<IUser>(`${this.resourceUrl}/id/${userId}`, { observe: 'response' });
    }

    getOnlineUsersCount(tenant: string, filter?: string): Observable<HttpResponse<number>> {
        return this.http.get<number>(`${this.resourceUrl}/online-count/${tenant}/filter/${filter}`, {
            observe: 'response',
            headers: hideProgress(),
        });
    }

    getOnlineUsers(tenant: string, filter?: string): Observable<HttpResponse<UserCache[]>> {
        return this.http.get<UserCache[]>(`${this.resourceUrl}/online-users/${tenant}/filter/${filter}`, {
            observe: 'response',
            headers: hideProgress(),
        });
    }

    removeImage(username: string) {
        return this.http.delete(`${this.resourceUrl}/${username}/remove/image`, {
            headers: logRequest(),
        });
    }

    updateImage64(dto: UserImageBase64VM): Observable<HttpResponse<any>> {
        return this.http.post('uaa/api/users/image64', dto, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    autoComplete(search: IUserAutoComplete): Observable<HttpResponse<KeyValueDTO[]>> {
        return this.http.post<KeyValueDTO[]>(`${this.resourceUrl}/autocomplete`, search, { observe: 'response' });
    }

    getImage(username: string): Observable<Image> {
        return this.http.get<Image>(`uaa/api/users/image/${username}`);
    }

    getImagePath(usernameOrId: string): Observable<Image> {
        return this.http.get<Image>(`uaa/api/users/image-path/${usernameOrId}`);
    }

    createProperty(id: string, property: UserProperty): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.resourceUrl}/${id}/property`, property, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    createProperties(id: string, properties: UserProperty[]): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.resourceUrl}/${id}/properties`, properties, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    mergeProperty(id: string, property: UserProperty): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.resourceUrl}/${id}/property-merge`, property, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    updateProperty(id: string, property: UserProperty): Observable<HttpResponse<IUser>> {
        return this.http.put<IUser>(`${this.resourceUrl}/${id}/property`, property, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    queryProperties(id: string): Observable<UserProperty[]> {
        return this.http.get<UserProperty[]>(`${this.resourceUrl}/${id}/properties`);
    }

    deleteProperty(id: string, key: string): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.resourceUrl}/${id}/property/${key}`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    deleteProperties(id: string, keys: string[]): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.resourceUrl}/${id}/properties/${keys.join(',')}`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    removePropertyValue(dto: RemovePropertyValueVM): Observable<HttpResponse<any>> {
        return this.http.put<any>(`${this.resourceUrl}/remove-property-value`, dto, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    /**
     * Find users have property
     * @param tenantId
     * @param property
     */
    findPropertyExistUsers(tenantId: string, property: UserProperty): Observable<HttpResponse<IUser[]>> {
        return this.http.put<IUser[]>(`${this.resourceUrl}/property/${tenantId}/exist-users`, property, {
            observe: 'response',
        });
    }

    findPropertyWithRoleIdExistUsers(
        tenantId: string,
        roleId: string,
        property: UserProperty
    ): Observable<HttpResponse<IUser[]>> {
        return this.http.put<IUser[]>(`${this.resourceUrl}/property/${tenantId}/${roleId}/exist-users`, property, {
            observe: 'response',
        });
    }

    /**
     * Find users have property
     * @param tenantId
     * @param property
     */
    findByPropertyValuesExistUsers(tenantId: string, property: UserPropertyVM): Observable<HttpResponse<IUser[]>> {
        return this.http.put<IUser[]>(`${this.resourceUrl}/property/${tenantId}/values-exist-users`, property, {
            observe: 'response',
        });
    }

    /**
     * Find users have not property values
     * @param tenantId
     * @param property
     */
    findByPropertyNotInValues(tenantId: string, property: UserPropertyVM): Observable<HttpResponse<IUser[]>> {
        return this.http.put<IUser[]>(`${this.resourceUrl}/property/${tenantId}/values-not-exist-users`, property, {
            observe: 'response',
        });
    }

    /**
     * Find users have not property value
     * @param tenantId
     * @param property
     */
    findByPropertyNotInValue(tenantId: string, property: UserPropertyVM): Observable<HttpResponse<IUser[]>> {
        return this.http.put<IUser[]>(`${this.resourceUrl}/property/${tenantId}/values-not-exist-users`, property, {
            observe: 'response',
        });
    }

    findByPropertyNotExistKey(tenantId: string, key: string): Observable<HttpResponse<IUser[]>> {
        return this.http.get<IUser[]>(`${this.resourceUrl}/property/${tenantId}/${key}/key-not-exist`, {
            observe: 'response',
        });
    }

    sendActivationLink(username: string): Observable<HttpResponse<any>> {
        return this.http.get<any>(`uaa/api/users/send-activation/${username}`, {
            observe: 'response',
            headers: logRequest(),
        });
    }

    resetPassword(mail: string): Observable<any> {
        return this.http.post('uaa/api/account/reset-password/init', mail, {
            headers: logRequest(),
        });
    }

    checkUserConnectionStatus(userId: string): Observable<boolean> {
        return this.http.get<boolean>(`/auth/user-connection-status/${userId}`);
    }

    getUserStatus(userId: string, tenantId: string): Observable<boolean> {
        return this.http.get<boolean>(`${this.resourceUrl}/status/${userId}/${tenantId}`);
    }

    getContacts(userId: string): Observable<UserCache[]> {
        return this.http.get<UserCache[]>(`${this.resourceUrl}/${userId}/contacts`);
    }

    isEmailAvailable(email: string): Observable<boolean> {
        return this.http.get<boolean>(`${this.resourceUrl}/email-check/${email}`);
    }

    updateStatus(userId: string, tenantId: string, status: string): Observable<boolean> {
        return this.http.post<any>(`${this.resourceUrl}/activation/${tenantId}/${userId}/${status}`, {
            observe: 'response',
            headers: logRequest(),
        });
    }
}
